import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
  Container,
  Typography,
  Grid,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CustomAppBar from "../../components/AppBar";
import api from "../../globals/axios";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import { t } from "i18next";
import CustomCardStatus from "../../components/Custom/CustomCardStatus";

const DEFAULT_LOCATION = {
  latitude: 19.070869,
  longitude: 72.858805,
};

const ServiceProviders = () => {
  const [serviceType, setServiceType] = useState(""); // Default option
  const [serviceProviders, setServiceProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchServiceProvidersWithLocation = async (lat, lng) => {
      try {
        const response = await api.get("/farmer/nearby-service-providers/", {
          params: {
            lat: lat,
            lng: lng,
            service_type: serviceType,
          },
        });

        setServiceProviders(response.data.service_providers);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    const fetchUserLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            fetchServiceProvidersWithLocation(latitude, longitude);
          },
          () => {
            // If location access fails, use default location
            fetchServiceProvidersWithLocation(
              DEFAULT_LOCATION.latitude,
              DEFAULT_LOCATION.longitude
            );
          }
        );
      } else {
        // If geolocation is not supported, use default location
        fetchServiceProvidersWithLocation(
          DEFAULT_LOCATION.latitude,
          DEFAULT_LOCATION.longitude
        );
      }
    };

    fetchUserLocation();
  }, [serviceType]);

  const handleServiceTypeChange = (event) => {
    setServiceType(event.target.value);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Typography variant="h6" color="error">
          {`Error: ${error}`}
        </Typography>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>Service Providers - Farmapp</title>
      </Helmet>
      <CustomAppBar />
      <Container>
        <CustomTitle>{t("Service Providers")}</CustomTitle>
        <Box sx={{ mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>{t("Select Service Type")}</InputLabel>
            <Select
              value={serviceType}
              label={t("Select Service Type")}
              onChange={handleServiceTypeChange}
            >
              <MenuItem value="input_supply">{t("Input Supply")}</MenuItem>
              <MenuItem value="machines">{t("Machines")}</MenuItem>
              <MenuItem value="warehouses">{t("Warehouses")}</MenuItem>
              <MenuItem value="mills">{t("Mills")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Grid container>
          {serviceProviders.length === 0 ? (
            <Box
              sx={{
                height: "80vh",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{ mb: 1, textAlign: "center" }}
              >
                {t("No Service Providers found nearby.")}
              </Typography>
            </Box>
          ) : (
            <Grid container>
              {serviceProviders.map((provider) => (
                <Grid item xs={12} key={provider.id}>
                  <CustomCardStatus
                    title={`${
                      provider.owner.name
                    } - ${provider.distance.toFixed(2)} ${t("km away")}`}
                    subtitle={`${provider.owner.phone} , ${provider.owner.address}`}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
      </Container>
    </>
  );
};

export default ServiceProviders;
